import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import Calculator from "@/components/site/Calculator";
import RoutingBlock from "@/components/site/RoutingBlock";
import { useCalculatorConfig, usePartners } from "@/hooks/useSiteData";
import { B340_SPECIALTIES, OG } from "@/lib/content";

const Caveat = ({ children, testId }) => (
  <div className="border-l-2 border-signal bg-bone pl-4 py-2" data-testid={testId}>
    <p className="text-sm text-signal leading-relaxed">{children}</p>
  </div>
);

export default function ThreeFortyB() {
  const { data: config } = useCalculatorConfig();
  const { data: partners } = usePartners();

  const responsibilities = [
    { who: "The hospital (covered entity)", items: ["Holds 340B eligibility and HRSA registration", "Owns the patient records the program is built on", "Dispenses through its 340B pharmacy", "Retains the 340B savings, lawfully"] },
    { who: "Your practice", items: ["Prescribes on medical necessity, exactly as today", "Provides care and documentation for your patients", "Keeps full clinical authority", "Signs the affiliation agreement — and nothing more onerous"] },
    { who: "What changes in the room", items: ["Nothing about your clinical judgment", "Prescriptions route to the covered-entity pharmacy", "Coordination and follow-up are handled for you", "Enrollment is voluntary and never changes the prescription"] },
  ];

  const compliance = [
    ["Duplicate-discount prevention", "We prevent a drug from being counted for both a 340B discount and a Medicaid rebate — the error HRSA looks for first."],
    ["Diversion controls", "340B-purchased drugs go only to eligible patients of the covered entity. We track it so it stays that way."],
    ["Inventory and split-billing support", "Virtual or physical inventory kept clean, with split-billing that survives an audit."],
    ["Audit readiness", "The audit file is built as you go, not assembled in a panic when HRSA calls."],
    ["HRSA recertification support", "Annual recertification is tracked and prepared so eligibility never lapses."],
  ];

  return (
    <>
      <Seo
        title="340B pharmacy through hospital-system partners"
        description="You don't need to qualify for 340B. Our hospital partners already do. Affiliation, patient definition, and audit-grade compliance for high-drug-cost specialties."
        path="/340b"
        ogImage={OG.b340}
      />

      {/* Network-first hero */}
      <Band tone="spruce">
        <Eyebrow>340B pharmacy</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          You don't need to qualify for 340B. Our hospital partners already do.
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl mb-8">
          340B eligibility is held by a covered entity — a hospital that meets HRSA's rules. A private
          practice does not become 340B-eligible on its own, and no honest company will tell you it can.
          What we do is connect your practice to a hospital partner that is already a covered entity.
        </p>
        <div className="flex flex-wrap gap-2">
          {B340_SPECIALTIES.map((s) => (
            <span key={s} className="tabular text-xs text-bone border border-sage/50 rounded-[2px] px-3 py-1.5">{s}</span>
          ))}
        </div>
      </Band>

      {/* Which hospitals / markets */}
      <Band tone="bone">
        <Eyebrow>Which hospitals, which markets</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-tight mb-8 max-w-2xl">
          The network answers the question you're already asking.
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-sage/40 border border-sage/40 rounded-[2px] overflow-hidden">
          {(partners || []).map((p) => (
            <div key={p.id} className="bg-bone p-6" data-testid={`b340-partner-${p.id}`}>
              <div className="tabular text-xs text-sage uppercase tracking-wider mb-2">{p.market}</div>
              <h3 className="font-display text-2xl text-spruce mb-3">{p.name}</h3>
              <p className="text-sm text-ink/80 leading-relaxed">{p.summary}</p>
            </div>
          ))}
        </div>
        <Link to="/network" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-spruce hover:text-brass transition-colors" data-testid="b340-network-link">
          Full network and geography <ArrowRight size={16} />
        </Link>
      </Band>

      {/* How affiliation works */}
      <Band tone="ink">
        <Eyebrow>How affiliation actually works</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-10 max-w-2xl">
          Who is responsible for what.
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {responsibilities.map((r, i) => (
            <div key={i} className="border-t border-sage/40 pt-5" data-testid={`b340-resp-${i}`}>
              <h3 className="text-lg font-semibold mb-4">{r.who}</h3>
              <ul className="space-y-2.5">
                {r.items.map((it, j) => (
                  <li key={j} className="text-sm text-sage leading-relaxed flex gap-2">
                    <span className="text-brass mt-0.5">—</span><span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Band>

      {/* Patient definition — mandatory */}
      <Band tone="bone">
        <Eyebrow>Patient definition, in plain language</Eyebrow>
        <div className="max-w-prose">
          <p className="text-base text-ink leading-relaxed mb-4">
            340B pricing applies to a <strong>patient of the covered entity</strong>. In practice that
            means the hospital partner maintains records of that patient's care — the relationship is
            real and documented, not a label applied at the pharmacy counter.
          </p>
          <p className="text-base text-ink leading-relaxed mb-6">
            This is the most audited element of an arrangement like this one. We cover it openly because
            a program run properly has nothing to hide here, and because you will read it exactly that
            way. The covered entity is, and stays, the 340B partner. Your practice never becomes the
            covered entity.
          </p>
          <Caveat testId="b340-patient-caveat">
            If any vendor implies your practice itself becomes 340B-eligible, stop the conversation. That
            is not how the program works, and it is the fastest way into an audit finding.
          </Caveat>
        </div>
      </Band>

      {/* Compliance infrastructure */}
      <Band tone="spruce">
        <Eyebrow>Compliance infrastructure</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4 max-w-2xl">
          Your real fear is an audit, not a missed margin.
        </h2>
        <p className="text-bone/85 text-base max-w-2xl mb-10">
          So this is built for audit first. Here is what runs underneath the program.
        </p>
        <div className="grid sm:grid-cols-2 gap-px bg-sage/30 border border-sage/30 rounded-[2px] overflow-hidden">
          {compliance.map(([t, d], i) => (
            <div key={i} className="bg-spruce p-6" data-testid={`b340-compliance-${i}`}>
              <div className="tabular text-brass text-sm mb-2">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="text-lg font-semibold mb-2">{t}</h3>
              <p className="text-sm text-bone/80 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Band>

      {/* Calculator */}
      <Band tone="bone">
        <Eyebrow>Illustrative math</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-tight mb-4 max-w-2xl">
          Start from your annual specialty drug spend.
        </h2>
        <p className="text-base text-ink/80 max-w-2xl mb-10">
          The precondition is on the same screen as the number, because the number means nothing without it.
        </p>
        {config && <Calculator config={config} mode="b340" />}
      </Band>

      {/* Is this you */}
      <Band tone="ink">
        <Eyebrow>Is this you?</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-10 max-w-2xl">
          If 340B isn't your fit, we'll say so.
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <RoutingBlock
            testId="b340-route-rpm"
            title="Most of your revenue is patient visits?"
            items={["You run a high-volume chronic-care panel", "Specialty drugs are a small share of revenue", "You're internal medicine, family practice, cardiology, or endocrinology"]}
            to="/rpm"
            cta="Look at RPM instead"
          />
          <RoutingBlock
            testId="b340-route-mso"
            title="Worried about the staff work?"
            items={["You can see the margin but not the bandwidth", "You don't want to hire for compliance and billing", "You want to know exactly what gets handled for you"]}
            to="/mso"
            cta="See what we handle"
          />
        </div>
      </Band>
    </>
  );
}
